/**
 * FiltersDialog — manage the account's filters. Lists rules with enable toggle +
 * delete, and a create form supporting MULTIPLE ANDed conditions (add/remove
 * rows) plus a single action. Filters apply Fias-side over newly-synced mail.
 */

import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Trash2, Plus, X } from 'lucide-react';
import type { MailboxFilterCondition, MailboxFilterAction } from '@/lib/api';
import {
  useEmailFilters,
  useCreateFilter,
  useToggleFilter,
  useDeleteFilter,
} from '../hooks/useFilterRules';
import { useEmailCategories } from '../hooks/useMailbox';
import { useDialogAccount } from '../hooks/useDialogAccount';
import { DialogAccountPicker } from './DialogAccountPicker';

interface FiltersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Seed account; the dialog has its own in-popup picker to switch. */
  defaultAccountId?: string;
}

const FIELDS: MailboxFilterCondition['field'][] = [
  'from',
  'to',
  'subject',
  'body',
  'has_attachment',
];
const FIELD_LABELS: Record<MailboxFilterCondition['field'], string> = {
  from: 'From',
  to: 'To',
  subject: 'Subject',
  body: 'Body',
  has_attachment: 'Has attachment',
};
const OPERATORS: MailboxFilterCondition['operator'][] = [
  'contains',
  'not_contains',
  'equals',
  'starts_with',
  'ends_with',
  'matches_regex',
];
const ACTIONS: MailboxFilterAction['type'][] = [
  'mark_read',
  'mark_starred',
  'archive',
  'trash',
  'set_category',
];

/** Actions that take a free-text value (others ignore it). */
const VALUED_ACTIONS = new Set<MailboxFilterAction['type']>(['set_category']);

/** A blank condition row, seeded on load and after a filter is added. */
const emptyCondition = (): MailboxFilterCondition => ({
  field: 'from',
  operator: 'contains',
  value: '',
});

/** One-line human summary of a saved condition for the rule list. */
function describeCondition(c: MailboxFilterCondition): string {
  if (c.field === 'has_attachment') {
    return `has attachment ${c.value.trim().toLowerCase() === 'true' ? 'present' : 'absent'}`;
  }
  return `${FIELD_LABELS[c.field] ?? c.field} ${c.operator.replace(/_/g, ' ')} “${c.value}”`;
}

export function FiltersDialog({ open, onOpenChange, defaultAccountId }: FiltersDialogProps) {
  const { accounts, selectedAccountId, setSelectedAccountId } = useDialogAccount(defaultAccountId);
  const { data: filters, isLoading } = useEmailFilters(open ? selectedAccountId : undefined);
  const { data: categories } = useEmailCategories(open);
  const create = useCreateFilter(selectedAccountId ?? '');
  const toggle = useToggleFilter(selectedAccountId ?? '');
  const del = useDeleteFilter(selectedAccountId ?? '');

  const [name, setName] = useState('');
  const [conditions, setConditions] = useState<MailboxFilterCondition[]>([emptyCondition()]);
  const [actionType, setActionType] = useState<MailboxFilterAction['type']>('mark_read');
  const [actionValue, setActionValue] = useState('');
  // Timed filter: run on a daily schedule over inbox mail older than N days.
  const [scheduled, setScheduled] = useState(false);
  const [runDays, setRunDays] = useState(7);

  const updateCondition = (i: number, patch: Partial<MailboxFilterCondition>) =>
    setConditions((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const addCondition = () => setConditions((prev) => [...prev, emptyCondition()]);
  const removeCondition = (i: number) =>
    setConditions((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));
  // has_attachment carries a true/false value with a fixed operator; a text field
  // resets to a blank free-text value when switched in.
  const changeField = (i: number, nextField: MailboxFilterCondition['field']) =>
    updateCondition(
      i,
      nextField === 'has_attachment'
        ? { field: nextField, operator: 'equals', value: 'true' }
        : { field: nextField, value: '' },
    );

  // A condition counts once it can match: has_attachment always does; a text
  // field needs a non-empty value.
  const isConditionReady = (c: MailboxFilterCondition) =>
    c.field === 'has_attachment' || c.value.trim().length > 0;
  const readyConditions = conditions.filter(isConditionReady);
  const canAdd = name.trim().length > 0 && readyConditions.length > 0 && !create.isPending;

  const add = () => {
    if (!canAdd) return;
    create.mutate(
      {
        name: name.trim(),
        conditions: readyConditions.map((c) => ({
          field: c.field,
          operator: c.field === 'has_attachment' ? 'equals' : c.operator,
          value: c.field === 'has_attachment' ? c.value : c.value.trim(),
        })),
        actions: [
          { type: actionType, value: VALUED_ACTIONS.has(actionType) ? actionValue.trim() : '' },
        ],
        runAfterDays: scheduled ? runDays : null,
      },
      {
        onSuccess: () => {
          setName('');
          setConditions([emptyCondition()]);
          setActionValue('');
          setScheduled(false);
          setRunDays(7);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Filters</DialogTitle>
        </DialogHeader>
        {!selectedAccountId ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Connect an account to manage filters.
          </p>
        ) : (
          <div className="space-y-4">
            <DialogAccountPicker
              accounts={accounts}
              value={selectedAccountId}
              onChange={setSelectedAccountId}
            />
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : !filters || filters.length === 0 ? (
              <p className="text-sm text-muted-foreground">No filters yet.</p>
            ) : (
              <ul className="space-y-1">
                {filters.map((f) => (
                  <li
                    key={f.filterId}
                    className="flex items-center justify-between gap-2 rounded border px-2 py-1 text-sm"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{f.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {f.conditions.map(describeCondition).join(' and ')} →{' '}
                        {f.actions.map((a) => a.type).join(', ')}
                        {f.runAfterDays ? ` · after ${f.runAfterDays}d` : ''}
                      </span>
                    </span>
                    <span className="flex items-center gap-1 shrink-0">
                      <label className="flex items-center gap-1 text-xs text-muted-foreground">
                        <input
                          type="checkbox"
                          checked={f.isEnabled}
                          onChange={(e) =>
                            toggle.mutate({ filterId: f.filterId, isEnabled: e.target.checked })
                          }
                        />
                        on
                      </label>
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Delete"
                        onClick={() => del.mutate(f.filterId)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <div className="space-y-2 border-t pt-3">
              <Input
                placeholder="Filter name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <div className="space-y-2">
                <span className="text-xs text-muted-foreground">If ALL of these match:</span>
                {conditions.map((c, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
                    <select
                      className="h-8 rounded-md border bg-background px-2"
                      aria-label="Field"
                      value={c.field}
                      onChange={(e) =>
                        changeField(i, e.target.value as MailboxFilterCondition['field'])
                      }
                    >
                      {FIELDS.map((f) => (
                        <option key={f} value={f}>
                          {FIELD_LABELS[f]}
                        </option>
                      ))}
                    </select>
                    {c.field === 'has_attachment' ? (
                      <select
                        className="h-8 rounded-md border bg-background px-2"
                        aria-label="Attachment presence"
                        value={c.value}
                        onChange={(e) => updateCondition(i, { value: e.target.value })}
                      >
                        <option value="true">is present</option>
                        <option value="false">is absent</option>
                      </select>
                    ) : (
                      <>
                        <select
                          className="h-8 rounded-md border bg-background px-2"
                          aria-label="Operator"
                          value={c.operator}
                          onChange={(e) =>
                            updateCondition(i, {
                              operator: e.target.value as MailboxFilterCondition['operator'],
                            })
                          }
                        >
                          {OPERATORS.map((o) => (
                            <option key={o} value={o}>
                              {o.replace(/_/g, ' ')}
                            </option>
                          ))}
                        </select>
                        <Input
                          className="w-40"
                          placeholder="value"
                          value={c.value}
                          onChange={(e) => updateCondition(i, { value: e.target.value })}
                        />
                      </>
                    )}
                    {conditions.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        title="Remove condition"
                        aria-label="Remove condition"
                        onClick={() => removeCondition(i)}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" className="gap-1" onClick={addCondition}>
                  <Plus className="h-3.5 w-3.5" /> Add condition
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-muted-foreground">then</span>
                <select
                  className="h-8 rounded-md border bg-background px-2"
                  value={actionType}
                  onChange={(e) => setActionType(e.target.value as MailboxFilterAction['type'])}
                >
                  {ACTIONS.map((a) => (
                    <option key={a} value={a}>
                      {a.replace('_', ' ')}
                    </option>
                  ))}
                </select>
                {actionType === 'set_category' && (
                  <select
                    className="h-8 w-44 rounded-md border bg-background px-2"
                    aria-label="Category"
                    value={actionValue}
                    onChange={(e) => setActionValue(e.target.value)}
                  >
                    <option value="">Choose category…</option>
                    {(categories ?? []).map((c) => (
                      <option key={c.categoryId} value={c.categoryId}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {/* Timed filter: run daily over aged inbox mail (matching the
                  conditions) instead of on new mail. */}
              <label className="flex flex-wrap items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5"
                  checked={scheduled}
                  onChange={(e) => setScheduled(e.target.checked)}
                />
                <span>Run on a schedule — apply to inbox mail older than</span>
                <Input
                  type="number"
                  min={1}
                  max={365}
                  className="h-8 w-16"
                  aria-label="Days"
                  value={runDays}
                  disabled={!scheduled}
                  onChange={(e) =>
                    setRunDays(Math.max(1, Math.min(365, Number(e.target.value) || 1)))
                  }
                />
                <span>days</span>
              </label>
              <Button size="sm" className="gap-1" disabled={!canAdd} onClick={add}>
                <Plus className="h-3.5 w-3.5" /> Add filter
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
